import React from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { ChartOfAccountDetailsEntity } from '../../../../../api/types/settings/accounting/chart-of-accounts/chart-of-account-details.type';
import { PaymentMethodsDb } from '../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import {
  JournalEntriesWaitingForReportFilterType,
  defaultJournalEntriesWaitingForReportFilter,
} from '../journal-entries-waiting-for-report-filter.type';

interface JournalEntriesWaitingForReportFilterProps {
  filters: JournalEntriesWaitingForReportFilterType;
  chartOfAccounts: ChartOfAccountDetailsEntity[];
  loading?: boolean;
  onChange: (filters: JournalEntriesWaitingForReportFilterType) => void;
  onSearch: () => void;
  onClear?: () => void;
}

const paymentMethodOptions = [
  { label: 'כרטיס אשראי', value: PaymentMethodsDb.CreditCard },
  { label: 'מזומן', value: PaymentMethodsDb.Cash },
  { label: 'הנחה', value: PaymentMethodsDb.Discount },
  { label: 'העברה בנקאית', value: PaymentMethodsDb.BankTransfer },
  { label: 'מלגה', value: PaymentMethodsDb.Scholarship },
  { label: 'המחאה', value: PaymentMethodsDb.Check },
];

export const JournalEntriesWaitingForReportFilter: React.FC<
  JournalEntriesWaitingForReportFilterProps
> = ({ filters, chartOfAccounts, loading, onChange, onSearch, onClear }) => {
  const update = <K extends keyof JournalEntriesWaitingForReportFilterType>(
    key: K,
    value: JournalEntriesWaitingForReportFilterType[K],
  ) => {
    onChange({ ...filters, [key]: value });
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      return;
    }
    onChange(defaultJournalEntriesWaitingForReportFilter());
  };

  const chartOfAccountOptions = chartOfAccounts.map((acc) => ({
    label: `${acc.accountName}${acc.accountCode ? ` (${acc.accountCode})` : ''}`,
    value: acc.id,
  }));

  const styles = {
    container: {
      background: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    title: {
      fontSize: '1.1rem',
      fontWeight: 600 as const,
      color: '#333',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: '12px',
    },
    field: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px',
    },
    label: {
      fontSize: '0.8rem',
      color: '#555',
    },
    inputFull: {
      width: '100%',
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: '12px',
      borderTop: '1px solid #eee',
      paddingTop: '12px',
    },
    buttonsRow: {
      display: 'flex',
      gap: '8px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>סינונים</div>

      <div style={styles.grid}>
        <div style={styles.field}>
          <label style={styles.label}>אמצעי תשלום</label>
          <MultiSelect
            value={filters.paymentMethod}
            options={paymentMethodOptions}
            onChange={(e) => update('paymentMethod', e.value ?? [])}
            placeholder="הכל"
            display="chip"
            style={styles.inputFull}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>מתאריך</label>
          <Calendar
            value={filters.fromDate}
            onChange={(e) => update('fromDate', (e.value as Date) ?? null)}
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="מתאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>עד תאריך</label>
          <Calendar
            value={filters.toDate}
            onChange={(e) => update('toDate', (e.value as Date) ?? null)}
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="עד תאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>חשבונות</label>
          <MultiSelect
            value={filters.chartOfAccountIds}
            options={chartOfAccountOptions}
            onChange={(e) => update('chartOfAccountIds', e.value ?? [])}
            placeholder="הכל"
            display="chip"
            filter
            emptyMessage="לא נמצאו חשבונות"
            emptyFilterMessage="לא נמצאו חשבונות"
            style={styles.inputFull}
          />
        </div>
      </div>

      <div style={styles.footer}>
        <div style={styles.buttonsRow}>
          <Button
            icon="pi pi-filter-slash"
            label="נקה"
            text
            severity="secondary"
            onClick={handleClear}
            disabled={loading}
          />
          <Button
            icon="pi pi-search"
            label="חפש"
            onClick={onSearch}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
